import type { AuthError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

export async function fetchCurrentSession(client?: SupabaseClient) {
  const supabase = client ?? getSupabaseClient();
  try {
    return await supabase.auth.getSession();
  } catch (err) {
    // getSession() can throw (e.g. Web Locks AbortError) instead of
    // returning { error }.  Normalise to the expected shape so callers
    // that destructure { data } don't crash.
    // eslint-disable-next-line no-console
    console.error(
      '[fetchCurrentSession] getSession() threw:',
      err instanceof Error ? `${err.name}: ${err.message}` : String(err),
    );
    return {
      data: { session: null },
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
}

export async function fetchAccessToken(client?: SupabaseClient): Promise<string | null> {
  const { data } = await fetchCurrentSession(client);
  return data?.session?.access_token ?? null;
}

type AuthClientOption = { supabase?: SupabaseClient };

export async function signInWithAzureOAuth(params: {
  redirectTo?: string;
  forcePrompt?: boolean;
  loginHint?: string | null;
  supabase?: SupabaseClient;
}): Promise<{ error: AuthError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const queryParams: Record<string, string> = {};
  if (params.forcePrompt) queryParams.prompt = 'select_account';
  if (params.loginHint) queryParams.login_hint = params.loginHint;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'azure',
    options: {
      redirectTo: params.redirectTo,
      ...(Object.keys(queryParams).length ? { queryParams } : {}),
    },
  });
  return { error: error ?? null };
}

export async function updateUserPassword(
  newPassword: string,
  options?: AuthClientOption,
): Promise<{ error: AuthError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error: error ?? null };
}

export async function setAuthSession(
  accessToken: string,
  refreshToken: string,
  options?: AuthClientOption,
): Promise<{ error: AuthError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  });
  return { error: error ?? null };
}

export async function signOutUser(
  options?: AuthClientOption,
): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  await supabase.auth.signOut();
}

export { createGuestAuthClient } from '../lib/supabaseClient';
